import { useMemo } from 'react';
import {
  Link as ReactRouterLink,
  To,
  useSearchParams,
  type LinkProps as ReactRouterLinkProps,
} from "react-router";
import { Language } from '~/localization/resource';
export interface LinkProps extends ReactRouterLinkProps {
  keepSearchParams?: boolean;
  language?: Language;
}

 const useEnhancedTo = ({
  language,
  to,
  keepSearchParams,
}: {
  language?: Language;
  to: To;
  keepSearchParams?: boolean;
}) => {
  const [params] = useSearchParams();
  const { lng, ...searchParams } = Object.fromEntries(params.entries());
  // allow language override for language switcher or manually setting the language in specific cases
  const lang = language ?? lng;
  const newSearchParams = new URLSearchParams(searchParams);
  const searchString = newSearchParams.toString();
  const hasSearchParams = searchString.length > 0;
  const appendSearchParams = lang || hasSearchParams;
  const newPath = useMemo(
    () =>
      to +
      (appendSearchParams
        ? `?${keepSearchParams && hasSearchParams ? `${searchString}${lang ? "&" : ""}` : ""}${lang ? `lng=${lang}` : ""}`
        : ""),
    [
      to,
      appendSearchParams,
      keepSearchParams,
      hasSearchParams,
      searchString,
      lang,
    ]
  );
  return newPath;
};
export const Link = ({
  prefetch = "intent",
  viewTransition = true,
  keepSearchParams = false,
  to,
  language,
  ...props
}: LinkProps) => {
  const enhancedTo = useEnhancedTo({ language, to, keepSearchParams });
  return (
    <ReactRouterLink
      prefetch={prefetch}
      viewTransition={viewTransition}
      to={enhancedTo}
      {...props}
    />
  );
};
